import { getUrl } from '@muse-web/url'
import { type TScreenData, navigate } from '@muse-web/router'
import { httpRawClient, isHttpError } from '@core/http-client'
import { ScreenId } from '@core/router'
import { sessionDialogStore } from '@core/session-dialogs'
import { getNextjsOrigin } from '../config/nextjsAppConfig'

// The router's screen payload, with `id` narrowed to this product's screens. `meta`/`jsonld` ride
// along and the router applies them to <head> on navigation.
export type TScreenLoaderData = TScreenData<ScreenId>

const isScreenId = (value: unknown): value is ScreenId =>
    typeof value === 'string' &&
    Object.values<string>(ScreenId).includes(value) &&
    ScreenId.ERROR !== value

export const buildNextjsHref = (href: string, nextjsOrigin: string): string | null => {
    if (!nextjsOrigin) {
        return null
    }

    const target = new URL(href)
    const nextjs = new URL(nextjsOrigin)

    if (nextjs.origin === target.origin) {
        return href
    }

    // Rebuilt rather than mutated: assigning `host` alone keeps the port this origin came with.
    return new URL(`${target.pathname}${target.search}${target.hash}`, nextjs.origin).toString()
}

const isScreenEnvelope = (value: unknown): value is TScreenLoaderData =>
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    isScreenId(value.id) &&
    'data' in value &&
    typeof value.data === 'object' &&
    value.data !== null

const PAGE_DATA_TIMEOUT_MS = 30_000

// One retry, and only when the request never reached the backend: a 5xx must not be repeated, since
// the response it carries is the answer (and `logError` already reports it).
const PAGE_DATA_RETRY = { limit: 1, statusCodes: [] }

const requestScreenData = async (path: string) => {
    try {
        const data = await httpRawClient
            .get(path, {
                credentials: 'include',
                timeout: PAGE_DATA_TIMEOUT_MS,
                retry: PAGE_DATA_RETRY,
            })
            .json<TScreenLoaderData>()

        return { data, errorBody: undefined }
    } catch (error) {
        if (isHttpError(error)) {
            return {
                data: undefined,
                errorBody: await error.response.json().catch(() => undefined),
            }
        }

        return { data: undefined, errorBody: undefined }
    }
}

export const getLoaderData = async (): Promise<TScreenLoaderData> => {
    // SPA navigation: backend returns the screen { id, data } JSON instead of the HTML shell
    const path = getUrl({ url: document.location.href, params: { json_response: 1 } })

    const { data, errorBody } = await requestScreenData(path)

    if (data) {
        return data
    }

    // A deliberate error screen (a missing audio → 404 + `audio-not-found`) rides in the body of a
    // non-2xx response, so the body is read before the status is treated as a failure — otherwise it
    // would be replaced by the generic error screen on soft nav.
    if (isScreenEnvelope(errorBody)) {
        return errorBody
    }

    // Only soft navigations reach this: a document request that 404s is redirected by the web tier
    // itself, before the SPA boots.
    const nextjsHref = buildNextjsHref(document.location.href, getNextjsOrigin())

    if (!nextjsHref) {
        return { id: ScreenId.ERROR, data: {} }
    }

    sessionDialogStore.updateState({ pendingNextjsExit: true })
    navigate(nextjsHref, { isForceHard: true })

    // Hard redirect is in flight — never resolve so the router does not commit / re-render a screen.
    return new Promise<TScreenLoaderData>(() => {
        //
    })
}
